/**
 * An axis for values from 0 to `max`: a clean step (1, 2, 2.5 or 5 times a power of ten; whole
 * numbers only for counts), a top that is a whole number of steps, and its ticks.
 */
export function axis(
  max: number,
  { count = 4, integer = false }: { count?: number; integer?: boolean } = {},
): { top: number; ticks: number[] } {
  const raw = (max > 0 ? max : 1) / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const steps = integer && power < 10 ? [1, 2, 5, 10] : [1, 2, 2.5, 5, 10];
  let step = (steps.find((s) => s * power >= raw) ?? 10) * power;
  if (integer) step = Math.max(1, Math.round(step));
  const top = Number(
    Math.max(step, Math.ceil((max > 0 ? max : step) / step) * step).toPrecision(12),
  );
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) =>
    Number((i * step).toPrecision(12)),
  );
  return { top, ticks };
}

/** Runs of consecutive points without a gap, for drawing a line that breaks at nulls. */
export function segments(
  points: readonly (number | null)[],
): { start: number; values: number[] }[] {
  const runs: { start: number; values: number[] }[] = [];
  let run: { start: number; values: number[] } | null = null;
  points.forEach((value, i) => {
    if (value === null || !Number.isFinite(value)) {
      run = null;
      return;
    }
    if (!run) {
      run = { start: i, values: [] };
      runs.push(run);
    }
    run.values.push(value);
  });
  return runs;
}
